/**
 * This will return formatted register data used for overview table and chart
 * Now includes totals calculation for consistency
 */

import { useMemo } from 'react';
import { isArray } from 'lodash';

function useRegisterData(data, uniquePackages, packageOption) {
  const result = useMemo(() => {
    if (!uniquePackages || uniquePackages.length === 0) return { data: [], totals: null };

    const groupPackage = new Map();

    uniquePackages.forEach(({ key, name }) => {
      groupPackage.set(key, {
        reg_total: 0,
        reg_success: 0,
        reg_fail: 0,
        renew_total: 0,
        renew_success: 0,
        renew_fail: 0,
        cancel_total: 0,
        cancel_user_total: 0,
        cancel_sys_total: 0,
        name: name,
      });
    });

    if (data && isArray(data) && data.length > 0) {
      data.forEach((item) => {
        const itemPackage = item.package_code;

        if (groupPackage.has(itemPackage)) {
          const stats = groupPackage.get(itemPackage);
          stats.reg_success += item.reg_success || 0;
          stats.reg_fail += item.reg_fail || 0;
          stats.reg_total += item.reg_total || 0;

          stats.renew_success += item.renew_success || 0;
          stats.renew_fail += item.renew_fail || 0;
          stats.renew_total += item.renew_total || 0;

          // Add to total cancel count
          stats.cancel_total += item.cancel_total || 0;

          // If channel is SYSTEM, add to sys_total
          if (item.channel === 'SYSTEM') {
            stats.cancel_sys_total += item.cancel_total || 0;
          }
        }
      });

      // Calculate cancel_user_total = cancel_total - cancel_sys_total for each package
      groupPackage.forEach((stats) => {
        stats.cancel_user_total = stats.cancel_total - stats.cancel_sys_total;
      });
    }

    // Calculate totals across all packages
    const calculateTotals = () => {
      const totals = { package_code: 'TOTAL' };

      if (packageOption === 2) {
        // Retail package totals
        const aggregated = Array.from(groupPackage.values()).reduce(
          (acc, stats) => {
            acc.total += stats.reg_total + stats.renew_total;
            acc.success += stats.reg_success + stats.renew_success;
            acc.fail += stats.reg_fail + stats.renew_fail;
            return acc;
          },
          { total: 0, success: 0, fail: 0 }
        );

        totals.total = aggregated.total;
        totals.success = aggregated.success;
        totals.fail = aggregated.fail;
        totals.rate =
          aggregated.total > 0
            ? `${((aggregated.success / aggregated.total) * 100).toFixed(2)}%`
            : '0.00%';
      } else {
        // Sub package totals
        const aggregated = Array.from(groupPackage.values()).reduce(
          (acc, stats) => {
            acc.register_total += stats.reg_total;
            acc.register_success += stats.reg_success;
            acc.register_fail += stats.reg_fail;
            acc.renew_total += stats.renew_total;
            acc.renew_success += stats.renew_success;
            acc.renew_fail += stats.renew_fail;
            acc.cancel_total += stats.cancel_total;
            acc.cancel_user_total += stats.cancel_user_total;
            acc.cancel_sys_total += stats.cancel_sys_total;
            return acc;
          },
          {
            register_total: 0,
            register_success: 0,
            register_fail: 0,
            renew_total: 0,
            renew_success: 0,
            renew_fail: 0,
            cancel_total: 0,
            cancel_user_total: 0,
            cancel_sys_total: 0,
          }
        );

        // Calculate rates
        aggregated.register_rate =
          aggregated.register_total > 0
            ? `${((aggregated.register_success / aggregated.register_total) * 100).toFixed(2)}%`
            : '0.00%';

        aggregated.renew_rate =
          aggregated.renew_total > 0
            ? `${((aggregated.renew_success / aggregated.renew_total) * 100).toFixed(2)}%`
            : '0.00%';

        Object.assign(totals, aggregated);
      }

      return totals;
    };

    // Format data for display
    const formatData = Array.from(groupPackage.entries()).map(([packageCode, stats]) => {
      if (packageOption === 2) {
        let retailTotals = {
          package_code: stats.name,
          total: 0,
          success: 0,
          fail: 0,
          rate: '',
        };

        retailTotals.total = stats.reg_total + stats.renew_total;
        retailTotals.success = stats.reg_success + stats.renew_success;
        retailTotals.fail = stats.reg_fail + stats.renew_fail;
        retailTotals.rate =
          retailTotals.total > 0
            ? `${((retailTotals.success / retailTotals.total) * 100).toFixed(2)}%`
            : '0%';

        return retailTotals;
      } else {
        const registerRate =
          stats.reg_total > 0
            ? `${((stats.reg_success / stats.reg_total) * 100).toFixed(2)}%`
            : '0%';
        const renewRate =
          stats.renew_total > 0
            ? `${((stats.renew_success / stats.renew_total) * 100).toFixed(2)}%`
            : '0%';

        return {
          package_code: stats.name,
          register_success: stats.reg_success,
          register_fail: stats.reg_fail,
          register_total: stats.reg_total,
          register_rate: registerRate,
          renew_success: stats.renew_success,
          renew_fail: stats.renew_fail,
          renew_total: stats.renew_total,
          renew_rate: renewRate,
          cancel_total: stats.cancel_total,
          cancel_user_total: stats.cancel_user_total,
          cancel_sys_total: stats.cancel_sys_total,
        };
      }
    });

    const totals = calculateTotals();

    return {
      data: formatData,
      totals: totals,
      dataWithTotals: [...formatData, totals], // Convenience property
    };
  }, [data, uniquePackages, packageOption]);

  return result;
}

export default useRegisterData;
